import {
  getManagedTemplateBySource,
  getTemplateVersion,
  getTemplateVersions,
  templateDownloadUrl,
  templateRenderPdfEndpoint,
} from "./api";
import { selectTemplateOpeningVersion } from "./templateVersionSelection";

function templateCallUrl() {
  if (import.meta.env.VITE_TEMPLATE_CALL_URL) return import.meta.env.VITE_TEMPLATE_CALL_URL;
  const localHost = /^(localhost|127\.0\.0\.1|\[::1\]|::1)$/i.test(window.location.hostname);
  if (localHost) {
    // 本地开发默认联调 18082，但运行时动态拼接，避免公网包携带本机地址。
    return `${window.location.protocol}//${["127", "0", "0", "1"].join(".")}:18082/`;
  }
  // 公网未配置独立文书生成地址时回落到当前站点，由底座或反向代理接管。
  return window.location.origin + "/";
}

function cleanTemplateName(value = "") {
  return String(value)
    .replace(/\.(docx?|pdf|html?)$/i, "")
    .replace(/_\d{12,}$/g, "")
    .trim();
}

function firstText(...values) {
  return values.map((value) => String(value || "").trim()).find(Boolean) || "";
}

function searchParamsFrom(search = "") {
  if (search instanceof URLSearchParams) return search;
  return new URLSearchParams(String(search || "").replace(/^\?/, ""));
}

export function isYukuaiqianSignFileDirectSelection(search = typeof window !== "undefined" ? window.location.search : "") {
  const params = searchParamsFrom(search);
  return params.get("directDocumentHub") === "1"
    && params.get("sourceSystem") === "yukuaiqian"
    && params.get("documentSelectMode") === "yukuaiqian-sign-file"
    && params.get("documentSelectTarget") === "sign-file"
    && params.get("workflowDeliveryMode") === "parent";
}

function yukuaiqianDirectParentOrigin(params) {
  const value = params.get("parentOrigin");
  if (!value) return "";
  try {
    return new URL(value, window.location.href).origin;
  } catch {
    return "";
  }
}

function absoluteCurrentOriginUrl(value) {
  const text = String(value || "").trim();
  if (!text) return "";
  try {
    return new URL(text, window.location.origin).href;
  } catch {
    return text;
  }
}

function templateExtension(file = {}) {
  return String(file.extension || file.fileName?.split(".").pop() || "")
    .replace(/^\./, "")
    .toLowerCase();
}

function directSelectionFileName(file = {}, context = {}) {
  const sourceName = firstText(file.fileName, context.template?.fileName, file.displayName, context.selectedTemplateName, "系统模版");
  if (/\.[A-Za-z0-9]{1,8}$/i.test(sourceName)) return sourceName;
  const extension = templateExtension(file);
  return extension ? `${sourceName}.${extension}` : sourceName;
}

function sourceMimeType(file = {}) {
  const extension = templateExtension(file);
  if (extension === "html" || extension === "htm") return "text/html;charset=utf-8";
  if (extension === "pdf") return "application/pdf";
  if (extension === "docx") return "application/vnd.openxmlformats-officedocument.wordprocessingml.document";
  if (extension === "doc") return "application/msword";
  return "";
}

function isRenderPdfEndpoint(value = "") {
  return /\/render-pdf(?:[?#]|$)/.test(String(value || ""));
}

function yukuaiqianRenderPdfEndpoint(file = {}, context = {}) {
  const endpoint = firstText(context.template?.renderPdfEndpoint);
  if (isRenderPdfEndpoint(endpoint)) return absoluteCurrentOriginUrl(endpoint);
  return absoluteCurrentOriginUrl(templateRenderPdfEndpoint(context.documentId || file.id || ""));
}

function publishedPdfUrlFromTemplate(template = {}) {
  return [
    template.publishedPdfUrl,
    template.renderedPdfUrl,
    template.pdfUrl,
    template.renderPdfEndpoint,
  ]
    .map((value) => String(value || "").trim())
    .find((value) => value && !isRenderPdfEndpoint(value)) || "";
}

function variableKeysFromHtml(html = "") {
  const keys = new Set();
  String(html || "").replace(/\{\{\s*([A-Za-z0-9_.:-]+)\s*\}\}/g, (_, key) => {
    keys.add(key);
    return "";
  });
  return Array.from(keys);
}

async function loadHtmlSourceTemplate(file = {}, signal) {
  if (!file.filePath || !["html", "htm"].includes(templateExtension(file))) return "";
  try {
    const response = await fetch(templateDownloadUrl(file.filePath), { cache: "no-store", signal });
    if (!response.ok) return "";
    return await response.text();
  } catch {
    return "";
  }
}

function blobToBase64Content(blob) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const text = String(reader.result || "");
      resolve(text.includes(",") ? text.split(",").pop() : text);
    };
    reader.onerror = () => reject(reader.error || new Error("文件读取失败"));
    reader.readAsDataURL(blob);
  });
}

async function yukuaiqianDirectSourceFilePayload(file = {}, fileName = "", editableHtml = "") {
  if (editableHtml || !file.filePath) return {};
  try {
    const response = await fetch(templateDownloadUrl(file.filePath), { cache: "no-store" });
    if (!response.ok) return {};
    const blob = await response.blob();
    return {
      sourceFileContent: await blobToBase64Content(blob),
      sourceFileMime: blob.type || sourceMimeType(file) || "application/octet-stream",
      sourceFileName: fileName || directSelectionFileName(file),
    };
  } catch {
    return {};
  }
}

function yukuaiqianDirectVariableValues(params) {
  const keys = [
    "companyName",
    "companyCreditCode",
    "socialCreditCode",
    "unifiedSocialCreditCode",
    "creditCode",
    "companyAddress",
    "companyPhone",
    "legalRepresentative",
    "employeeName",
    "employeeNo",
    "staffName",
    "staffCode",
    "gender",
    "idNum",
    "phone",
    "mobile",
    "position",
    "department",
  ];
  return Object.fromEntries(
    keys
      .map((key) => [key, params.get(key) || ""])
      .filter(([, value]) => value),
  );
}

function invocationContext(file, company, companyRegion) {
  // 整理模板、企业和来源页面信息，供文书生成系统追踪本次调用。
  const templateName = file.displayName || cleanTemplateName(file.fileName) || file.templateName;
  const scenarioName = file.submoduleName || file.scenarioName || templateName || "文书";
  return {
    moduleName: scenarioName,
    scenarioName,
    companyId: company?.id || "",
    companyName: company?.name || "",
    companyCreditCode: firstText(
      company?.companyCreditCode,
      company?.socialCreditCode,
      company?.unifiedSocialCreditCode,
      company?.creditCode,
      company?.licenseNo,
      company?.profile?.companyCreditCode,
      company?.profile?.socialCreditCode,
      company?.profile?.unifiedSocialCreditCode,
      company?.profile?.creditCode,
      company?.profile?.licenseNo,
    ),
    companyAddress: firstText(company?.registeredAddress, company?.companyAddress, company?.address, company?.profile?.registeredAddress),
    companyRegion: companyRegion?.fullName || firstText(company?.region, company?.companyRegion, company?.profile?.region),
    companyIndustry: companyRegion?.industrySource || firstText(company?.industry, company?.companyIndustry, company?.industryType, company?.profile?.industry),
    sourceSystem: "document-template-hub",
    sourceUrl: window.location.href,
    documentHubBase: window.location.origin,
    documentId: file.id || "",
    templateKey: file.templateKey || file.id || "",
    selectedTemplateName: templateName,
    template: {
      id: file.id || "",
      templateKey: file.templateKey || file.id || "",
      templateName,
      templateType: file.templateType || file.kind || "system",
      templateSource: file.templateSource || "文书模板项目",
      moduleKey: file.moduleKey || "",
      moduleName: file.moduleName || "",
      submoduleKey: file.submoduleKey || "",
      submoduleName: file.submoduleName || file.scenarioName || "",
      fileName: file.fileName || "",
      filePath: file.filePath || "",
      renderPdfEndpoint: templateRenderPdfEndpoint(file.id || ""),
    }
  };
}

function sourceDocumentIdForManagedLookup(file) {
  if (file.sourceDocumentId) return file.sourceDocumentId;
  if (file.managedTemplateId) return "";
  return file.id || "";
}

function templateVariableKeys(value) {
  if (!Array.isArray(value)) return [];
  return value
    .map((item) => {
      if (typeof item === "string") return item;
      if (!item || typeof item !== "object") return "";
      return item.key || item.name || item.field || "";
    })
    .map((item) => String(item || "").trim())
    .filter(Boolean);
}

async function hydrateTemplateVersion(version, signal) {
  if (!version?.id) return null;
  if (version.signatureConfig || version.documentJson || version.editableHtml) return version;
  try {
    return await getTemplateVersion(version.id, signal);
  } catch (error) {
    if (error.name === "AbortError") throw error;
    return version;
  }
}

export async function resolvePublishedTemplateVersion(file, signal) {
  if (
    file.publishedVersion?.renderedPdfUrl
    || file.publishedVersion?.editableHtml
    || file.publishedVersion?.templateHtml
    || file.publishedVersion?.documentJson
  ) {
    return hydrateTemplateVersion(file.publishedVersion, signal);
  }

  if (file.managedTemplateId) {
    try {
      const payload = await getTemplateVersions(file.managedTemplateId, signal);
      const selected = selectTemplateOpeningVersion(payload);
      // 业务调用只能继承已发布版，不将平台端尚未确认的草稿透出给用户。
      return await hydrateTemplateVersion(selected.published, signal);
    } catch (error) {
      if (error.name === "AbortError") throw error;
      // 兼容旧接口：managedTemplateId 不可用时继续尝试 sourceDocumentId。
    }
  }

  const sourceDocumentId = sourceDocumentIdForManagedLookup(file);
  if (!sourceDocumentId) return null;
  const managed = await getManagedTemplateBySource(sourceDocumentId, signal);
  return hydrateTemplateVersion(managed.publishedVersion, signal);
}

export async function buildTemplateCallContext(file, company, companyRegion, signal) {
  const context = invocationContext(file, company, companyRegion);
  try {
    const published = await resolvePublishedTemplateVersion(file, signal);
    if (published) {
      context.template.templateVersionId = published.id;
      context.template.sourceSha256 = published.renderedPdfSha256 || published.sourceFileSha256;
      context.template.sourceFileSha256 = published.sourceFileSha256;
      context.template.renderedPdfSha256 = published.renderedPdfSha256;
      context.template.pageCount = published.pageCount;
      // 中文注释：调用端编辑必须使用未套值的已发布正文和变量定义；
      // PDF 只用于版式预览，否则会把已渲染数据再叠加成重复变量。
      context.template.templateHtml = published.editableHtml || published.templateHtml || "";
      context.template.editableHtml = published.editableHtml || "";
      context.template.documentJson = published.documentJson || null;
      context.template.variables = templateVariableKeys(published.variables);
      context.template.databaseTemplate = Boolean(file.databaseTemplate || file.templateCallDefault || file.databaseSource || file.sourceRecordId || file.managedTemplateId);
      context.template.requiresElectronicSignature = Boolean(published.requiresElectronicSignature);
      context.template.signatureConfig = published.signatureConfig || null;
      context.template.renderedPdfUrl = firstText(published.renderedPdfUrl, published.publishedPdfUrl, published.pdfUrl);
      context.template.publishedPdfUrl = context.template.renderedPdfUrl;
      context.template.renderPdfEndpoint = context.template.renderedPdfUrl || context.template.renderPdfEndpoint;
      context.template.renderPdfMethod = context.template.renderedPdfUrl ? "GET" : "POST";
    }
  } catch {
    // 尚未纳入版本管理的旧模板继续按原静态预览入口调用。
  }

  if (!context.template.editableHtml && !context.template.templateHtml) {
    const sourceHtml = await loadHtmlSourceTemplate(file, signal);
    if (sourceHtml) {
      context.template.templateHtml = sourceHtml;
      context.template.editableHtml = sourceHtml;
      context.template.variables = variableKeysFromHtml(sourceHtml);
    }
  }

  return context;
}

export async function selectTemplateForYukuaiqianSignFile(file, company, companyRegion) {
  const params = searchParamsFrom(typeof window !== "undefined" ? window.location.search : "");
  if (!isYukuaiqianSignFileDirectSelection(params)) return false;
  const parentOrigin = yukuaiqianDirectParentOrigin(params);
  if (!parentOrigin || window.parent === window) {
    throw new Error("当前渝快签父页面不可用，不能直接回填签署文件");
  }

  // 中文维护注释：仅渝快签系统模版直选通道跳过 18082 模板调用工作台，直接把选中的文书正文回填给父页面。
  const context = await buildTemplateCallContext(file, company, companyRegion);
  const template = context.template || {};
  const fileName = directSelectionFileName(file, context);
  const templateName = firstText(context.selectedTemplateName, template.templateName, cleanTemplateName(fileName), "系统模版");
  const renderPdfEndpoint = yukuaiqianRenderPdfEndpoint(file, context);
  const publishedPdfUrl = absoluteCurrentOriginUrl(publishedPdfUrlFromTemplate(template));
  const editableHtml = firstText(template.editableHtml, template.templateHtml);
  const templateHtml = firstText(template.templateHtml, editableHtml);
  const variableKeys = Array.isArray(template.variables) ? template.variables : [];
  const variableValues = yukuaiqianDirectVariableValues(params);
  const companyCreditCode = firstText(
    variableValues.companyCreditCode,
    variableValues.socialCreditCode,
    variableValues.unifiedSocialCreditCode,
    variableValues.creditCode,
    context.companyCreditCode,
  );
  const signFileParams = {
    ...variableValues,
    companyCreditCode,
    socialCreditCode: companyCreditCode,
    unifiedSocialCreditCode: companyCreditCode,
    creditCode: companyCreditCode,
  };
  const sourceFilePayload = await yukuaiqianDirectSourceFilePayload(file, fileName, editableHtml);
  const sourceFileMime = sourceFilePayload.sourceFileMime || sourceMimeType(file);
  const documentTemplate = {
    ...template,
    fileName,
    displayFileName: fileName,
    sourceDisplayFileName: fileName,
    sourceDocumentFileName: fileName,
    renderPdfEndpoint,
    renderedPdfUrl: publishedPdfUrl,
    publishedPdfUrl,
    editableHtml,
    templateHtml,
    documentJson: template.documentJson || null,
    variables: variableKeys,
  };
  const payload = {
    invocationId: params.get("invocationId") || "",
    sourceSystem: "document-template-hub",
    sourceProject: "文书模版",
    callerSystem: params.get("callerSystem") || "",
    workflowDeliveryMode: "parent",
    directDocumentHub: "1",
    documentHubMode: "library",
    documentSelectMode: "yukuaiqian-sign-file",
    documentSelectTarget: "sign-file",
    moduleName: context.moduleName,
    scenarioName: params.get("originalScenarioName") || context.scenarioName,
    sourceScenarioName: context.scenarioName,
    originalScenarioName: params.get("originalScenarioName") || "",
    documentId: context.documentId,
    templateKey: context.templateKey,
    templateName,
    fileName,
    displayFileName: fileName,
    sourceDisplayFileName: fileName,
    sourceDocumentFileName: fileName,
    sourceFileName: sourceFilePayload.sourceFileName || fileName,
    sourceFileMime,
    extension: templateExtension(file),
    companyId: context.companyId,
    companyName: context.companyName,
    companyCreditCode,
    socialCreditCode: companyCreditCode,
    unifiedSocialCreditCode: companyCreditCode,
    creditCode: companyCreditCode,
    companyAddress: context.companyAddress,
    companyRegion: context.companyRegion,
    companyIndustry: context.companyIndustry,
    renderPdfEndpoint,
    renderedPdfUrl: publishedPdfUrl,
    publishedPdfUrl,
    editableHtml,
    templateHtml,
    documentHtml: editableHtml,
    documentJson: template.documentJson || null,
    variables: variableKeys,
    params: signFileParams,
    documentTemplate,
    template: documentTemplate,
    workflowRequest: {
      templateName,
      sourceFileName: sourceFilePayload.sourceFileName || fileName,
      sourceFileMime,
      sourceFileExtension: templateExtension(file),
      sourceFileContent: sourceFilePayload.sourceFileContent || "",
      params: signFileParams,
    },
  };

  window.parent.postMessage(
    {
      type: "template-call:document-selected",
      version: 1,
      sourceSystem: "document-template-hub",
      invocationId: payload.invocationId,
      payload,
    },
    parentOrigin,
  );
  return true;
}

export async function openTemplateCall(file, company, companyRegion) {
  if (await selectTemplateForYukuaiqianSignFile(file, company, companyRegion)) return null;

  // 调用端只接收原文件渲染入口，正式预览由 render-pdf 生成的 PDF 承担。
  const context = await buildTemplateCallContext(file, company, companyRegion);
  const url = new URL(templateCallUrl(), window.location.href);
  url.searchParams.set("moduleName", context.moduleName);
  url.searchParams.set("scenarioName", context.scenarioName);
  url.searchParams.set("documentId", context.documentId);
  url.searchParams.set("templateKey", context.templateKey);
  url.searchParams.set("selectedTemplateName", context.selectedTemplateName);
  url.searchParams.set("sourceSystem", context.sourceSystem);
  url.searchParams.set("parentOrigin", window.location.origin);
  url.searchParams.set("returnUrl", window.location.href);
  url.searchParams.set("companyId", context.companyId || "");
  url.searchParams.set("companyName", context.companyName || "");
  url.searchParams.set("companyCreditCode", context.companyCreditCode || "");
  url.searchParams.set("socialCreditCode", context.companyCreditCode || "");
  url.searchParams.set("unifiedSocialCreditCode", context.companyCreditCode || "");
  url.searchParams.set("creditCode", context.companyCreditCode || "");
  url.searchParams.set("companyAddress", context.companyAddress || "");
  url.searchParams.set("region", context.companyRegion || "");
  url.searchParams.set("companyRegion", context.companyRegion || "");
  url.searchParams.set("industry", context.companyIndustry || "");
  url.searchParams.set("companyIndustry", context.companyIndustry || "");
  url.searchParams.set("templateSource", context.template.templateSource || "");
  url.searchParams.set("renderPdfEndpoint", new URL(context.template.renderPdfEndpoint, window.location.origin).href);
  url.searchParams.set("renderPdfMethod", context.template.renderPdfMethod || "POST");
  url.searchParams.set("templateFilePath", context.template.filePath || "");
  if (context.template.templateVersionId) {
    const invocationResponse = await fetch(new URL("api/v1/invocations", templateCallUrl()), {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
      ...context,
      template: context.template,
      documentTemplate: context.template,
      templateVersionId: context.template.templateVersionId,
      sourceSha256: context.template.sourceSha256,
      pageCount: context.template.pageCount,
      variables: context.template.variables,
      requiresElectronicSignature: context.template.requiresElectronicSignature,
      signatureConfig: context.template.signatureConfig,
      }),
    });
    if (!invocationResponse.ok) throw new Error("模板调用上下文创建失败");
    const invocation = await invocationResponse.json();
    url.searchParams.set("invocationId", invocation.invocationId);
  }
  window.location.assign(url.toString());
  return null;
}
